import humanFileSize from "./humanFileSize";
import { cornerstone } from "./cornerstone/cornerstone-setup.js";
import {
  recordHeapUsage,
  recordOPFSUsage,
  recordCacheSnapshot,
} from "./session-stats";

let currentStage = "idle";
let totalFrames = 0;

export function updateMemoryStage(stage: string, frames: number = 0) {
  currentStage = stage;
  if (frames > 0) totalFrames = frames;
}

function getCornerstoneCacheInfo(): {
  imagesInMemory: number;
  cacheSizeBytes: number;
} | null {
  try {
    const cache = (cornerstone as any).imageCache;
    if (!cache) return null;
    const info = cache.getCacheInfo?.() ?? null;
    if (info) {
      return {
        imagesInMemory: info.numberOfImagesCached ?? 0,
        cacheSizeBytes: info.cacheSizeInBytes ?? 0,
      };
    }
    return null;
  } catch {
    return null;
  }
}

async function getOPFSInfo(): Promise<{
  usage: number;
  quota: number;
} | null> {
  try {
    const est = await navigator.storage.estimate();
    if (est.usage != null && est.quota != null) {
      return { usage: est.usage, quota: est.quota };
    }
    return null;
  } catch {
    return null;
  }
}

export default function monitorMemory(element: HTMLElement) {
  let memoryMin: number | null = null;
  let memoryMax = 0;

  const update = async () => {
    if (!element) return;
    const el = element;
    const performance = window.performance as any;

    const statusLines: string[] = [];

    if (performance.memory) {
      const memoryLimit = humanFileSize(performance.memory.jsHeapSizeLimit);
      const memoryAllocated = humanFileSize(
        performance.memory.totalJSHeapSize
      );
      const memoryUsed = humanFileSize(performance.memory.usedJSHeapSize);

      if (performance.memory.totalJSHeapSize > memoryMax)
        memoryMax = performance.memory.totalJSHeapSize;
      if (!memoryMin) {
        memoryMin = performance.memory.totalJSHeapSize;
      }
      if (memoryMin && performance.memory.totalJSHeapSize < memoryMin)
        memoryMin = performance.memory.totalJSHeapSize;

      recordHeapUsage(
        performance.memory.usedJSHeapSize,
        performance.memory.jsHeapSizeLimit
      );

      statusLines.push(
        `JS Heap: ${memoryUsed} / ${memoryAllocated} / ${memoryLimit}`
      );
      statusLines.push(
        `Heap Min: ${memoryMin && humanFileSize(memoryMin)}  Max: ${
          memoryMax && humanFileSize(memoryMax)
        }`
      );
    }

    const csInfo = getCornerstoneCacheInfo();
    if (csInfo) {
      statusLines.push(
        `CS Cache: ${csInfo.imagesInMemory} imgs (${humanFileSize(csInfo.cacheSizeBytes)})`
      );
      // The image cache is now the thing that bounds memory, so its peak belongs
      // in the diagnostic report — it was already computed here and discarded.
      recordCacheSnapshot(csInfo.cacheSizeBytes, csInfo.imagesInMemory);
    }

    const opfsInfo = await getOPFSInfo();
    if (opfsInfo) {
      statusLines.push(
        `OPFS: ${humanFileSize(opfsInfo.usage)} / ${humanFileSize(opfsInfo.quota)}`
      );
      recordOPFSUsage(opfsInfo.usage, opfsInfo.quota);
    }

    statusLines.push(`Frames: ${totalFrames} total`);
    statusLines.push(`Stage: ${currentStage}`);

    if (el?.innerHTML !== undefined) {
      el.innerHTML = statusLines.map((s) => `<div>${s}</div>`).join("");
    }
  };

  update();
  window.setInterval(update, 500);
}

export async function logMemorySnapshot(
  label: string,
  frames: number = 0
) {
  try {
    const performance = window.performance as any;
    const parts: string[] = [];

    if (performance.memory) {
      parts.push(
        `heapUsed=${humanFileSize(performance.memory.usedJSHeapSize)}`
      );
      parts.push(
        `heapTotal=${humanFileSize(performance.memory.totalJSHeapSize)}`
      );
    }

    const csInfo = getCornerstoneCacheInfo();
    if (csInfo) {
      parts.push(
        `csCache=${csInfo.imagesInMemory}imgs(${humanFileSize(csInfo.cacheSizeBytes)})`
      );
    }

    const opfsInfo = await getOPFSInfo();
    if (opfsInfo) {
      parts.push(
        `opfs=${humanFileSize(opfsInfo.usage)}/${humanFileSize(opfsInfo.quota)}`
      );
    }

    if (frames > 0) {
      parts.push(`frames=${frames}`);
    }

    console.log(
      `%c[${label}]%c ${parts.join(" ")}`,
      "font-weight:bold;color:#3DD0FC",
      ""
    );

    updateMemoryStage(label, frames);
  } catch (e) {
    console.warn("logMemorySnapshot error:", e);
  }
}
